import { useState, createContext, useContext, ReactNode } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type ButtonType = "link" | "default" | "destructive" | "destructiveFilled" | "outline" | "primary" | "secondary" | "ghost" | null | undefined;
// Define the shape of the dialog options
export interface DialogOptions {
  title: ReactNode;
  desc?: ReactNode;
  icon?: ReactNode;
  iconClass?: string;
  action: {label: string, id: string, type: ButtonType}[],
  confirmText?: string;
  cancelText?: string;
  onConfirm?: (action?: {label: string, id: string, type: ButtonType}) => void;
  onCancel?: (action?: {label: string, id: string, type: ButtonType}) => void;
  dialogClassName?: string;
  // Opt-in "fill" layout: makes the dialog a flex column and lets `desc` stretch to
  // the dialog's height (used by the report preview, which sizes itself to the
  // viewport). Off by default so every other dialog keeps its content-sized layout.
  fill?: boolean;
}

// Define the shape of the context value
interface DialogContextType {
  showDialog: (options: DialogOptions) => void;
  closeDialog: () => void;
}

// Create the context
const DialogContext = createContext<DialogContextType | undefined>(undefined);

// Create the provider component
export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [options, setOptions] = useState<DialogOptions | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const onDialogCancle = (action: {label: string, id: string, type: ButtonType}) => {
    options?.onCancel?.(action);
    setIsOpen(false);
  }

  const showDialog = (newOptions: DialogOptions) => {
    setOptions(newOptions);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setOptions(null);
  }

  return (
    <DialogContext.Provider value={{ showDialog, closeDialog }}>
      {children}
      {options && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className={cn('w-full', options.fill && 'flex flex-col min-h-0 overflow-hidden', options.dialogClassName)}>

              <div className={cn('flex flex-col items-start gap-3', options.fill && 'flex-1 min-h-0 w-full')}>
                {options.icon ? <div className={`shrink-0 mt-0.5 text-text-error ${options.iconClass||''}`}>{options.icon}</div> : null}
                <div className={cn('w-full min-w-0', options.fill && 'flex flex-col flex-1 min-h-0')}>
                  <DialogTitle className='pb-2'>{options.title}</DialogTitle>
                  {options.desc && (
                    typeof options.desc === 'string' 
                    ? <DialogDescription className="text-text-medium-emphasis w-full">{options.desc}</DialogDescription> 
                    : options.desc
                  )}
                </div>
              </div>

            {
              !!options.action.length && (
                <DialogFooter className="flex justify-center">
                {
                  options.action.map((action) => {
                    return (
                    <Button 
                      key={action.id} 
                      className="cursor-pointer"
                      variant={action.type || 'default'} 
                      onClick={()=>{action.id === 'CANCEL' ? onDialogCancle(action) : options?.onConfirm?.(action)}}>
                    {action.label}
                    </Button>
                    )
                  })
                }
              </DialogFooter>)
            }
            
          </DialogContent>
        </Dialog>
      )}
    </DialogContext.Provider>
  );
};

// Create the custom hook
export const useDialog = () => {
  const context = useContext(DialogContext);
  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};
